/**
 * Call routing API (AI receptionist live transfer, spec §5.1).
 *
 * ONE SHAPE PER FUNCTION, success and failure alike: every function resolves
 * the response BODY. On success that is the backend's `{ success: true, data }`;
 * on failure it is the backend's OWN failure body, untouched —
 * `{ success: false, code, error, ...details }` — so the settings page can act
 * on `code` (LOOP_NUMBER, INVALID_NUMBER, GROUP_IN_USE, CALLS_NOT_CONFIGURED…)
 * and its details (`numbers`, `user_ids`, `field`, `missing`). Nothing here
 * throws. A failure with no body (network down, timeout) becomes
 * `{ success: false, code: "NETWORK_ERROR", error }`.
 *
 * The HTTP status is added as `status` on failures, because two answers are
 * only told apart by it (503 not configured, 429 rate limited, 409 in use).
 */
import axios from "./axiosInstance";
import config from "./config";
import { getAuthToken, getTenantId, getAuthSource } from "./apiHelpers";

const BASE_URL = config.BASE_URL;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ApiFailure = {
  success: false;
  code?: string;
  error: string;
  status?: number;
  field?: string | null;
  numbers?: string[];
  user_ids?: string[];
  group_ids?: string[];
  missing?: string[];
};
export type ApiResult<T> = { success: true; data: T } | ApiFailure;

export type AvailabilityStatus = "available" | "busy" | "off";
export type RingStrategy = "simultaneous" | "sequential";
export type TerminalAction = "voicemail" | "callback_then_voicemail" | "message_hangup";
export type AfterHoursAction = TerminalAction | "ring_group";

export type FallbackStep =
  | { type: "group"; group_id: string }
  | {
      type: "number";
      e164: string;
      label?: string;
      timeout_seconds?: number;
      require_keypress?: boolean;
    };

export interface Closure {
  date: string;
  label?: string;
}

export interface CallRoutingSettings {
  id: string | null;
  enabled: boolean;
  business_hours: Record<string, unknown> | null;
  timezone: string | null;
  closures: Closure[];
  announce_press_zero: boolean;
  transfer_message: string | null;
  hold_message: string | null;
  hold_message_every_seconds: number;
  hold_music_url: string | null;
  max_wait_seconds: number;
  callback_enabled: boolean;
  callback_offer_after_seconds: number;
  fallback_chain: FallbackStep[];
  no_answer_action: TerminalAction;
  after_hours_action: AfterHoursAction;
  after_hours_group_id: string | null;
  after_hours_message: string | null;
  voicemail_prompt: string | null;
  voicemail_max_seconds: number;
  forwarding_lines: string[];
  task_assignee_user_id: string | null;
  notify_user_ids: string[];
  notify_email: boolean;
  delete_twilio_recordings: boolean;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface GroupMember {
  id?: string;
  member_kind: "staff" | "external";
  user_id: string | null;
  external_label: string | null;
  external_e164: string | null;
  position: number;
  ring_web: boolean;
  ring_app: boolean;
  ring_phone: boolean;
  require_keypress: boolean;
  active?: boolean;
}

export interface EscalationGroup {
  id: string;
  name: string;
  ring_strategy: RingStrategy;
  ring_timeout_seconds: number;
  member_timeout_seconds: number;
  active: boolean;
  intents?: string[];
  members: GroupMember[];
}

export interface GroupInput {
  name?: string;
  ring_strategy?: RingStrategy;
  ring_timeout_seconds?: number;
  member_timeout_seconds?: number;
  active?: boolean;
}

export interface RoutingSettingsBundle {
  settings: CallRoutingSettings;
  groups: EscalationGroup[];
  legacy: { transfer_phone_number: string | null };
}

export interface StaffAvailability {
  user_id: string;
  name: string;
  status: AvailabilityStatus;
  status_expires_at: string | null;
  presence: { web: boolean; app: boolean };
  on_call: boolean;
}

export interface MyAvailability {
  user_id: string;
  status: AvailabilityStatus;
  status_note: string | null;
  status_expires_at: string | null;
  presence: { web: boolean; app: boolean };
  on_call: boolean;
  on_call_session_id: string | null;
  /** The club's call routing switch (call_routing_settings.enabled); false with no settings. */
  routing_enabled: boolean;
}

export interface VoiceToken {
  token: string;
  identity: string;
  ttl_seconds: number;
}

export interface CallBrief {
  caller: {
    label: string;
    number: string | null;
    membership_id: string | null;
    lead_id: string | null;
    family_path: string | null;
  };
  escalation: { reason: string | null; summary: string | null; urgency: string | null };
  waited_seconds: number;
  office_open: boolean | null;
}

export interface Paged<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
}

export interface CallSessionSummary {
  id: string;
  created_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  state: string;
  outcome: string | null;
  caller_number: string | null;
  caller_number_source: string | null;
  caller_label: string;
  membership_id: string | null;
  lead_id: string | null;
  escalation_source: string | null;
  escalation_reason: string | null;
  escalation_urgency: string | null;
  office_open: boolean | null;
  answered_by_user_id: string | null;
  answered_by_name: string | null;
  ai_conversation_id: string | null;
}

export interface CallLeg {
  id: string;
  step_index: number;
  target_kind: "client_web" | "client_app" | "pstn";
  user_id: string | null;
  user_name: string | null;
  target: string | null;
  state: string;
  created_at: string;
  ringing_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
}

export interface CallMessage {
  id: string;
  call_session_id: string;
  kind: "voicemail" | "callback_request" | "missed_call";
  caller_number: string | null;
  callback_number: string | null;
  membership_id: string | null;
  lead_id: string | null;
  recording_storage_path: string | null;
  recording_duration_seconds: number | null;
  transcription: string | null;
  processing_status: string | null;
  ai_summary: string | null;
  urgency: string | null;
  status: "new" | "done";
  task_id: string | null;
  handled_by: string | null;
  handled_at: string | null;
  created_at: string;
  caller_label?: string;
  has_recording?: boolean;
}

export interface CallSessionDetail {
  session: CallSessionSummary & {
    escalation_summary: string | null;
    hold_started_at: string | null;
    answered_at: string | null;
    family_path: string | null;
    forwarded_from: string | null;
    to_number: string | null;
  };
  legs: CallLeg[];
  messages: CallMessage[];
  ai_conversation_id: string | null;
}

// ---------------------------------------------------------------------------
// Failure handling
// ---------------------------------------------------------------------------

/**
 * The backend's own failure body when there is one (with the HTTP status
 * added), else a network failure. Kept whole: dropping `numbers` or `missing`
 * would leave the page unable to say WHICH number loops.
 */
function failureBody(err: unknown): ApiFailure {
  const e = err as { response?: { status?: number; data?: unknown }; message?: string };
  const status = e?.response?.status;
  const body = e?.response?.data;
  if (body && typeof body === "object" && !Array.isArray(body)) {
    const b = body as Record<string, unknown>;
    return {
      ...(b as object),
      success: false,
      error:
        (typeof b.error === "string" && b.error) ||
        (typeof b.message === "string" && b.message) ||
        "Request failed",
      status,
    } as ApiFailure;
  }
  return {
    success: false,
    code: status ? "HTTP_ERROR" : "NETWORK_ERROR",
    error: e?.message || "Request failed",
    status,
  };
}

/** A 204 has no body; it still answers in the one shape. */
const NO_CONTENT: ApiResult<null> = { success: true, data: null };

// ---------------------------------------------------------------------------
// Settings + groups (manage_call_routing)
// ---------------------------------------------------------------------------

export const getRoutingSettings = (): Promise<ApiResult<RoutingSettingsBundle>> =>
  axios
    .get(`${BASE_URL}/calls/routing-settings`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const updateRoutingSettings = (
  patch: Partial<CallRoutingSettings>
): Promise<ApiResult<CallRoutingSettings>> =>
  axios
    .put(`${BASE_URL}/calls/routing-settings`, patch)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const createGroup = (group: GroupInput): Promise<ApiResult<EscalationGroup>> =>
  axios
    .post(`${BASE_URL}/calls/groups`, group)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const updateGroup = (id: string, group: GroupInput): Promise<ApiResult<EscalationGroup>> =>
  axios
    .put(`${BASE_URL}/calls/groups/${encodeURIComponent(id)}`, group)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const deleteGroup = (id: string): Promise<ApiResult<{ id: string; deleted: boolean }>> =>
  axios
    .delete(`${BASE_URL}/calls/groups/${encodeURIComponent(id)}`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

/** Replaces the group's whole member list (the backend accepts the bare array). */
export const replaceGroupMembers = (
  id: string,
  members: Array<Omit<GroupMember, "id" | "active">>
): Promise<ApiResult<EscalationGroup>> =>
  axios
    .put(`${BASE_URL}/calls/groups/${encodeURIComponent(id)}/members`, members)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

// ---------------------------------------------------------------------------
// Availability + presence (answer_calls)
// ---------------------------------------------------------------------------

export const listAvailability = (): Promise<ApiResult<StaffAvailability[]>> =>
  axios
    .get(`${BASE_URL}/calls/availability`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const getMyAvailability = (): Promise<ApiResult<MyAvailability>> =>
  axios
    .get(`${BASE_URL}/calls/availability/me`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const updateMyAvailability = (body: {
  status: AvailabilityStatus;
  status_expires_at?: string | null;
  status_note?: string | null;
}): Promise<ApiResult<MyAvailability>> =>
  axios
    .put(`${BASE_URL}/calls/availability/me`, body)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

/** The 30-second heartbeat. 204 on success; 429 RATE_LIMITED past 6 a minute. */
export const sendPresence = (registered: boolean): Promise<ApiResult<null>> =>
  axios
    .post(`${BASE_URL}/calls/presence`, { channel: "web", registered, client_version: "web" })
    .then(() => NO_CONTENT)
    .catch((err) => failureBody(err));

/**
 * Tell the server this browser stopped taking calls. Called while the page
 * unloads, so it uses fetch `keepalive` (axios cannot outlive the page).
 * Returns whether the request was handed to the browser; the answer itself
 * is never seen.
 */
export const sendPresenceOffline = (): boolean => {
  if (typeof fetch !== "function") return false;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenantId = getTenantId();
  if (tenantId) headers["x-tenant-id"] = tenantId;
  const source = getAuthSource();
  if (source) headers["x-auth-source"] = source;
  try {
    fetch(`${BASE_URL}/calls/presence/offline`, {
      method: "POST",
      headers,
      body: "{}",
      keepalive: true,
    }).catch(() => false);
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Devices (answer_calls)
// ---------------------------------------------------------------------------

/** A Twilio access token for this browser. 503 CALLS_NOT_CONFIGURED when the platform has no Twilio keys. */
export const getVoiceToken = (): Promise<ApiResult<VoiceToken>> =>
  axios
    .post(`${BASE_URL}/calls/voice-token`, { channel: "web" })
    .then((res) => res.data)
    .catch((err) => failureBody(err));

/** Ring this user's own browser and app once with a test message (202). */
export const testRing = (): Promise<ApiResult<unknown>> =>
  axios
    .post(`${BASE_URL}/calls/test-ring`, {})
    .then((res) => res.data)
    .catch((err) => failureBody(err));

// ---------------------------------------------------------------------------
// History (view_call_history) + the incoming-call brief
// ---------------------------------------------------------------------------

export const listCallSessions = (params: {
  page?: number;
  outcome?: string;
  from?: string;
  to?: string;
} = {}): Promise<ApiResult<Paged<CallSessionSummary>>> =>
  axios
    .get(`${BASE_URL}/calls/sessions`, { params })
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const getCallSession = (id: string): Promise<ApiResult<CallSessionDetail>> =>
  axios
    .get(`${BASE_URL}/calls/sessions/${encodeURIComponent(id)}`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const getCallBrief = (id: string): Promise<ApiResult<CallBrief>> =>
  axios
    .get(`${BASE_URL}/calls/sessions/${encodeURIComponent(id)}/brief`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const listCallMessages = (params: {
  page?: number;
  status?: "new" | "done";
  kind?: string;
} = {}): Promise<ApiResult<Paged<CallMessage>>> =>
  axios
    .get(`${BASE_URL}/calls/messages`, { params })
    .then((res) => res.data)
    .catch((err) => failureBody(err));

/** One message, in the list's item shape. 404 MESSAGE_NOT_FOUND when it is not this club's. */
export const getCallMessage = (id: string): Promise<ApiResult<CallMessage>> =>
  axios
    .get(`${BASE_URL}/calls/messages/${encodeURIComponent(id)}`)
    .then((res) => res.data)
    .catch((err) => failureBody(err));

export const updateCallMessage = (
  id: string,
  status: "new" | "done"
): Promise<ApiResult<CallMessage>> =>
  axios
    .patch(`${BASE_URL}/calls/messages/${encodeURIComponent(id)}`, { status })
    .then((res) => res.data)
    .catch((err) => failureBody(err));

/**
 * A 60-second signed URL for a voicemail. `format=json` because an <audio>
 * element cannot send the Authorization header the redirect form needs.
 */
export const getCallRecordingUrl = (
  id: string
): Promise<ApiResult<{ url: string; expires_in: number }>> =>
  axios
    .get(`${BASE_URL}/calls/messages/${encodeURIComponent(id)}/recording`, {
      params: { format: "json" },
    })
    .then((res) => res.data)
    .catch((err) => failureBody(err));
